import { experienceClient, loginClient } from "./axiosClient";

/**
 * Login bridge: RedTeam Xperience (Laravel admin, /api/v3) owns the student
 * accounts, so the portal never authenticates anyone itself. The flow is the
 * same three steps the Xperience web app uses:
 *
 *   1. request-otp        — rejects unknown/blocked accounts, emails an OTP
 *   2. verify-otp         — returns a Passport token proving identity
 *   3. exam-portal-sso    — mints an Experience SSO JWT (server-side; the HS256
 *                           secret is never exposed to this bundle)
 *
 * The JWT is then exchanged at the Go backend's `GET /api/auth/{jwt}`, which
 * creates the student in the portal database on first login and hands back the
 * portal session token.
 */

type Envelope<T> = { status: string; message: string; data: T };

export type ExperienceUser = {
  id: number;
  name: string;
  email: string;
  mobile: string | null;
  img_url: string;
};

/** Pull the human-readable reason out of the v3 `{status,message,data}` envelope. */
export function experienceErrorMessage(error: any, fallback: string): string {
  return error?.response?.data?.message || error?.message || fallback;
}

/** Step 1 — email in, OTP out. Errors when the account is unknown or disabled. */
export async function requestOtp(email: string): Promise<string> {
  const { data } = await experienceClient.post<Envelope<null>>(
    "/auth/request-otp",
    { email }
  );
  return data.message;
}

/** Step 2 — verify the OTP; returns the Xperience Passport token. */
export async function verifyOtp(
  email: string,
  otp: string
): Promise<{ token: string; user: ExperienceUser }> {
  const { data } = await experienceClient.post<
    Envelope<{ token: string; token_type: string; user: ExperienceUser }>
  >("/auth/verify-otp", { email, otp });
  return { token: data.data.token, user: data.data.user };
}

/** Step 3 — trade the Passport token for a single-use exam-portal SSO JWT. */
export async function requestPortalSsoJwt(
  experienceToken: string
): Promise<string> {
  const { data } = await experienceClient.post<Envelope<{ jwt: string }>>(
    "/auth/exam-portal-sso",
    {},
    { headers: { Authorization: `Bearer ${experienceToken}` } }
  );
  return data.data.jwt;
}

/**
 * Final step — exchange the SSO JWT for a portal session token. The Go backend
 * creates the user (permission `student`) when the email is new to the portal.
 */
export async function exchangeSsoJwt(jwt: string): Promise<string> {
  const { data } = await loginClient.get<{ authorization_token: string }>(
    `/auth/${jwt}`
  );
  if (!data.authorization_token) {
    throw new Error("Exam portal returned no session token.");
  }
  return data.authorization_token;
}
